<script lang="ts" setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import SvgIllustration from '@/components/SvgIllustrations/SvgIllustration.vue';
import { useI18n } from 'vue-i18n';
import {
  isOnboardingActive,
  isWhatsNewMode,
  useOnboarding
} from '@/composables/onBoarding/useOnboarding';
import { ROUTE_NAMES } from '@/router/conf';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import type { VOverlay } from 'vuetify/components';
import useUserService from '@/composables/apiServices/useUserService';

const { t } = useI18n();
const router = useRouter();
const { smAndDown } = useDisplay();
const overlayRef = useTemplateRef<VOverlay>('overlay');
const { details } = useUserService();
const { isLoading } = details();
const {
  onBoardingSteps,
  markOnboardingComplete,
  isPending,
  showNewAccountDialog,
  isWhatsNewAvailable
} = await useOnboarding();

const currentStepIndex = ref<number>(-1);
const scrimRect = ref<DOMRect | null>(null);

const SCRIM_PADDING = 8; // px of extra space around the highlighted target

const scrimStyle = computed(() => {
  if (!scrimRect.value) return {};
  const rect = scrimRect.value;
  return {
    '--zwm-onboarding-scrim-top': `${rect.top + rect.height / 2}px`,
    '--zwm-onboarding-scrim-left': `${rect.left + rect.width / 2}px`,
    '--zwm-onboarding-scrim-width': `${rect.width + SCRIM_PADDING * 2}px`,
    '--zwm-onboarding-scrim-height': `${rect.height + SCRIM_PADDING * 2}px`,
    '--zwm-onboarding-z-index': overlayRef.value?.rootEl
      ? getComputedStyle(overlayRef.value.rootEl).zIndex
      : '9999'
  };
});

const currentStep = computed(() => onBoardingSteps.value[currentStepIndex.value]);

const overlayProps = computed(() => ({
  target: currentStep.value?.targetSelector ?? undefined,
  locationStrategy: 'connected' as const,
  location: currentStep.value?.location ?? 'start',
  origin: currentStep.value?.origin ?? 'end top',
  offset: 32,
  absolute: true
}));

const currentNextLabel = computed(() => {
  return currentStepIndex.value + 1 === onBoardingSteps.value.length
    ? t('onBoarding.actions.done')
    : t('onBoarding.actions.next');
});

const handleAfterStep = async () => {
  if (currentStepIndex.value === -1) return;
  const currentStepAfter = onBoardingSteps.value[currentStepIndex.value]?.after;
  await currentStepAfter?.();
};

const activateStep = async (stepIndex: number) => {
  await handleAfterStep();
  if (stepIndex > onBoardingSteps.value.length - 1) {
    return;
  }

  // Get new step
  const newStep = onBoardingSteps.value[stepIndex];
  if (!newStep) return;

  await newStep.before?.();

  if (newStep.targetSelector) {
    const el = document.querySelector<HTMLElement>(newStep.targetSelector);
    if (!el) return;
    el.scrollIntoView({ block: 'nearest' });
    await nextTick();
    scrimRect.value = el.getBoundingClientRect();
  } else {
    scrimRect.value = null;
  }
  currentStepIndex.value = stepIndex;
};

const handleNext = async () => {
  if (currentStepIndex.value < onBoardingSteps.value.length - 1) {
    await activateStep(currentStepIndex.value + 1);
  } else {
    await handleAfterStep();
    await handleDone();
    await handleClose();
  }
};

const handleDone = async () => {
  await markOnboardingComplete();
};

const handleSkip = async () => {
  await handleDone();
  await handleClose();
};

const handleClose = async () => {
  await handleAfterStep();
  scrimRect.value = null;
  isWhatsNewMode.value = false;
  isOnboardingActive.value = false;
  if (router.currentRoute.value.name !== ROUTE_NAMES.BASE_LAYOUT_TAB) {
    await router.push({ name: ROUTE_NAMES.BASE_LAYOUT_TAB });
    await nextTick();
  }
  currentStepIndex.value = -1;
};

const handleInitOnBoarding = () => {
  if (smAndDown.value) {
    handleClose();
    return;
  }
  if (isLoading.value) {
    return;
  }
  if (isWhatsNewMode.value) {
    activateStep(0);
    return;
  }
  if (isWhatsNewAvailable.value) {
    return;
  }
  if (onBoardingSteps.value.length > 0) {
    isOnboardingActive.value = true;
  }
};

watch(
  [onBoardingSteps, smAndDown],
  () => {
    handleInitOnBoarding();
  },
  { immediate: true }
);

// When startWhatsNew() sets isOnboardingActive = true, the onBoardingSteps
// watch does not fire (steps didn't change). This watch handles that case.
watch(isOnboardingActive, async (active) => {
  if (active && isWhatsNewMode.value) {
    await activateStep(0);
  }
});
</script>

<template>
  <template v-if="isOnboardingActive">
    <v-dialog v-if="!currentStep && !isWhatsNewMode" :model-value="true" persistent>
      <v-card class="confirm-modal" sentry-unmask data-test-id="onboarding-modal">
        <div class="d-flex flex-column align-center">
          <SvgIllustration name="NoMail" width="175" height="175" />
          <h2 class="text-title-lg text-center">
            {{ showNewAccountDialog ? t('onBoarding.title') : t('onBoarding.updateTitle') }}
          </h2>
          <p class="text-center text-body-lg my-0">
            {{ t('onBoarding.description') }}
          </p>
        </div>
        <div class="d-flex flex-wrap ga-2 justify-center w-100 mt-6">
          <TextBtn data-test-id="onboarding-skip" @click="handleSkip" :disabled="isPending">
            {{ t('onBoarding.actions.skip') }}
          </TextBtn>
          <FilledBtn data-test-id="onboarding-start" @click="handleNext" :disabled="isPending">
            {{ t('onBoarding.actions.start') }}
          </FilledBtn>
        </div>
      </v-card>
    </v-dialog>
    <template v-else-if="currentStep">
      <!-- eslint-disable-next-line -->
      <div v-if="scrimRect" class="onboarding__scrim" :style="scrimStyle"></div>
      <v-overlay
        ref="overlay"
        :model-value="true"
        v-bind="overlayProps"
        :scrim="false"
        persistent
        retain-focus
        capture-focus
        z-index="9999"
      >
        <v-card
          class="confirm-modal onboarding"
          role="dialog"
          aria-labelledby="onBoardingTitle"
          aria-describedby="onBoardingText"
          data-test-id="onboarding-step-card"
          sentry-unmask
        >
          <CloseBtn
            :aria-label="t('common.close')"
            class="confirm-modal__close-btn"
            color="on-surface"
            data-test-id="delete-addressbook-close"
            icon
            :disabled="isPending"
            @click="handleSkip"
          >
            <v-icon>mdi-close</v-icon>
          </CloseBtn>
          <div class="d-flex ga-4 align-center onboarding-content">
            <div v-if="currentStep.icon" style="height: 129px; min-width: 129px">
              <SvgIllustration :name="currentStep.icon" :key="currentStep.icon" />
            </div>
            <div class="w-100">
              <h2 class="text-title-lg ma-0" id="onBoardingTitle">
                {{ currentStep.title }}
              </h2>
              <v-card-text class="py-4 px-0 text-body-lg" id="onBoardingText">
                {{ currentStep.text }}
              </v-card-text>
              <div class="confirm-modal__actions">
                <div class="dots">
                  <VIcon
                    v-for="(step, index) in onBoardingSteps.length"
                    :key="index"
                    :color="index === currentStepIndex ? 'primary' : 'outline'"
                    size="10px"
                  >
                    mdi-circle
                  </VIcon>
                </div>
                <FilledBtn data-test-id="onboarding-next" @click="handleNext" :disabled="isPending">
                  {{ currentNextLabel }}
                </FilledBtn>
              </div>
            </div>
          </div>
        </v-card>
      </v-overlay>
    </template>
  </template>
</template>

<style lang="scss" scoped>
@use '../../assets/styles/settings' as settings;
@use 'sass:map';
@use '@/assets/styles/generic/confirm-modal';

.dots {
  display: flex;
  gap: 0.1875rem;
  justify-content: center;
}

.onboarding {
  width: 32.5rem;

  @media screen and #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
    width: calc(100vw - 2rem);
    right: unset !important;
    left: 1rem !important;
    top: unset !important;
    bottom: 1rem !important;
  }

  &-content {
    &__skip {
      margin-right: auto;
    }
  }

  .confirm-modal__actions {
    justify-content: space-between;
    align-items: center;

    &:has(.onboarding-content__skip) {
      flex-direction: row;
    }
  }

  &__scrim {
    position: fixed;
    top: var(--zwm-onboarding-scrim-top);
    left: var(--zwm-onboarding-scrim-left);
    width: var(--zwm-onboarding-scrim-width);
    height: var(--zwm-onboarding-scrim-height);
    box-sizing: content-box;
    background: transparent;
    border: 100vmax solid rgb(0 0 0 / 32%);
    border-radius: 0.5rem;
    transform: translate(-50%, -50%);
    z-index: var(--zwm-onboarding-z-index, 1011);
    pointer-events: all;
    cursor: not-allowed;
  }
}
</style>
